function resolvePulseApiUrl(): string {
  const configured = (import.meta.env.VITE_API_URL as string | undefined)?.trim().replace(/\/$/, '');
  if (configured) return configured;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'admin.pulse.igentify.in' || host === 'pulse.igentify.in') {
      return 'https://api.pulse.igentify.in';
    }
  }
  return '';
}

const API_URL = resolvePulseApiUrl();

let idToken: string | null = null;
let refreshIdToken: (() => Promise<string | null>) | null = null;

export function setIdToken(token: string | null) {
  idToken = token;
}

/** Wire Cognito refresh so long-lived polls don't stick with an expired Bearer. */
export function setIdTokenRefresher(fn: () => Promise<string | null>) {
  refreshIdToken = fn;
}

async function req<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
  const headers = new Headers(init.headers || {});
  if (!headers.has('content-type') && init.body) headers.set('content-type', 'application/json');
  if (idToken) headers.set('authorization', `Bearer ${idToken}`);
  const res = await fetch(`${API_URL}${path}`, { ...init, headers });
  const text = await res.text();
  let data: Record<string, unknown> = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { error: text.slice(0, 200) };
  }
  if (res.status === 401 && !retried && refreshIdToken) {
    const { debugLog } = await import('./debugLog');
    debugLog('api.unauthorized', { path, method: init.method || 'GET', retried: false });
    const next = await refreshIdToken();
    if (next) return req<T>(path, init, true);
    debugLog('api.unauthorized_refresh_failed', { path });
  }
  if (!res.ok) {
    if (res.status === 401) {
      const { debugLog } = await import('./debugLog');
      debugLog('api.unauthorized', { path, method: init.method || 'GET', retried });
    }
    const err = String(data.error || res.statusText || `HTTP ${res.status}`);
    throw Object.assign(new Error(err), { status: res.status, path });
  }
  return data as T;
}

export type CustomerAgentInfo = {
  agentId: string;
  name: string;
  role: string;
  published?: boolean;
  avatarUrl?: string | null;
  videoAvatarEnabled?: boolean;
  /** LiveAvatar FULL — browser MediaRecorder archives when true. */
  clientSessionArchiveEnabled?: boolean;
  /** When false, complete page hides transcript summary and PDF download. Default on. */
  completePageTranscriptEnabled?: boolean;
  /**
   * Direct only: show animated insight cards under the user PiP when Live sources
   * tools return structured metrics. Off by default.
   */
  liveInsightCardsEnabled?: boolean;
  /** Planned session length (minutes) for customer prep copy on haircheck. */
  sessionMaxMinutes?: number | null;
};

export type ArchiveSlot = 'userAudio' | 'userVideo' | 'botAudio' | 'botVideo';

export const api = {
  authConfig: () =>
    req<{ region: string; userPoolId: string | null; userPoolClientId: string | null }>('/auth/config'),
  /** Limited agent display fields for the customer UI (no prompts). */
  getAgent: (agentId?: string, opts?: { version?: number | 'working' | null }) => {
    const q =
      opts?.version === 'working'
        ? '?version=working'
        : typeof opts?.version === 'number' && opts.version >= 1
          ? `?version=${opts.version}`
          : '';
    return agentId
      ? req<CustomerAgentInfo>(`/agents/${encodeURIComponent(agentId)}${q}`)
      : req<CustomerAgentInfo>(`/agents${q}`);
  },
  createLiveKitSession: (body: Record<string, unknown> = {}) =>
    req<{
      sessionId: string;
      room: string;
      url: string;
      userToken: string;
      botQueued?: boolean;
      agentMode?: 'cascade' | 'direct' | 'liveavatar' | string;
      liveAvatarFull?: boolean;
      clientSessionArchiveEnabled?: boolean;
      /** Direct insight cards — authoritative for this session (do not rely on stale getAgent). */
      liveInsightCardsEnabled?: boolean;
      sessionMaxMinutes?: number;
      sessionWarnMinutes?: number;
      /** Wall-clock cap (ms) stamped at LiveAvatar FULL mint — aligns UI + LA max_session_duration. */
      sessionLimitEndsAtMs?: number;
    }>('/sessions/livekit', { method: 'POST', body: JSON.stringify(body) }),
  /** FULL client archive — start multipart (or prepare single PUT). */
  archiveUploadStart: (
    sessionId: string,
    body: { slot: ArchiveSlot; contentType: string },
  ) =>
    req<{
      uploadId: string;
      key: string;
      partSizeHint: number;
      mode: 'multipart' | 'put';
      putUrl?: string;
    }>(`/sessions/${encodeURIComponent(sessionId)}/archive/uploads`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  archiveUploadPartUrl: (
    sessionId: string,
    uploadId: string,
    body: { partNumber: number },
  ) =>
    req<{ url: string; partNumber: number }>(
      `/sessions/${encodeURIComponent(sessionId)}/archive/uploads/${encodeURIComponent(uploadId)}/parts`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  archiveUploadComplete: (
    sessionId: string,
    uploadId: string,
    body: {
      parts?: Array<{ partNumber: number; etag: string }>;
      /** Single-PUT mode: object already written; just stamp recordingKeys. */
      finalizePut?: boolean;
    },
  ) =>
    req<{ ok: boolean; key?: string; slot?: string }>(
      `/sessions/${encodeURIComponent(sessionId)}/archive/uploads/${encodeURIComponent(uploadId)}/complete`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  signalSessionReady: (sessionId: string) =>
    req<{ ok: boolean; botQueued?: boolean; already?: boolean }>(
      `/sessions/${encodeURIComponent(sessionId)}/ready`,
      { method: 'POST', body: '{}' },
    ),
  rejoinSession: (sessionId: string, body: Record<string, unknown> = {}) =>
    req<{
      sessionId: string;
      room: string;
      url: string;
      userToken: string;
      botQueued?: boolean;
      needsReady?: boolean;
      status?: string;
      agentId?: string | null;
      transcriptLines?: Array<{
        role?: string;
        text?: string;
        at?: number;
        kind?: string;
        url?: string;
      }>;
      firstName?: string | null;
      lastName?: string | null;
      userName?: string | null;
    }>(`/sessions/${encodeURIComponent(sessionId)}/rejoin`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  postActivity: (sessionId: string) =>
    req<{ ok: boolean }>(`/sessions/${sessionId}/activity`, { method: 'POST', body: '{}' }),
  /** Client-side playback + publisher RCA (autoplay, webrtc stats, mute, visibility). */
  reportClientEvent: (
    sessionId: string,
    body: {
      type: string;
      message?: string;
      meta?: Record<string, unknown>;
      [key: string]: unknown;
    },
  ) =>
    req<{ ok: boolean }>(`/sessions/${encodeURIComponent(sessionId)}/client-event`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** LiveAvatar FULL only — persist one LaRoom data-channel line (worker does not join). */
  postTranscriptLine: (
    sessionId: string,
    body: { role: 'user' | 'assistant'; text: string; at?: number },
  ) =>
    req<{ ok: boolean; count?: number }>(
      `/sessions/${encodeURIComponent(sessionId)}/transcript`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  hangupSession: (
    sessionId: string,
    body?: {
      origin?: string;
      endReason?: string;
      explicit_user_hangup?: boolean;
      client_operation_id?: string;
      operation_id?: string;
      transcriptLines?: Array<{ role: string; text: string; at?: number }>;
    },
  ) =>
    req<{ ok: boolean; endReason?: string }>(`/sessions/${encodeURIComponent(sessionId)}/hangup`, {
      method: 'POST',
      body: JSON.stringify(body || {}),
    }),
  getSessionLive: (sessionId: string) =>
    req<{
      sessionId: string;
      status?: string;
      transcriptLines?: Array<{
        role?: string;
        text?: string;
        at?: number;
        kind?: string;
        url?: string;
      }>;
      durationSec?: number | null;
    }>(`/sessions/${sessionId}/live`),
  getSession: (sessionId: string) =>
    req<{
      sessionId: string;
      status?: string;
      transcripts?: Array<{ role?: string; text?: string; startMs?: number }>;
      transcriptLines?: Array<{ role?: string; text?: string; at?: number }>;
      durationSec?: number | null;
      createdAt?: string;
      userName?: string | null;
    }>(`/sessions/${sessionId}`),
};
